
import React, { useState, useEffect } from 'react';
import { Lead, BlogPost, FAQItem, GalleryImage, NewsletterSubscriber } from '../types';
import { StorageService } from '../services/db';

interface Props {
  leads: Lead[];
  onUpdateStatus: (id: string, status: 'read') => void;
  onExport: () => void;
  onSync: () => void;
}

export const AdminPanel: React.FC<Props> = ({ leads, onUpdateStatus, onExport, onSync }) => {
  const [activeTab, setActiveTab] = useState<'leads' | 'blog' | 'faq' | 'tags' | 'gallery' | 'newsletter' | 'settings'>('leads');
  const [blogs, setBlogs] = useState<BlogPost[]>(StorageService.getBlogs());
  const [faqs, setFaqs] = useState<FAQItem[]>(StorageService.getFAQs());
  const [tags, setTags] = useState<string[]>(StorageService.getTags());
  const [gallery, setGallery] = useState<GalleryImage[]>(StorageService.getGallery());
  const [subscribers, setSubscribers] = useState<NewsletterSubscriber[]>(StorageService.getSubscribers());
  const [googleKey, setGoogleKey] = useState(StorageService.getGoogleKey());

  // Form States
  const [newBlog, setNewBlog] = useState<Partial<BlogPost>>({ tags: [], images: [] });
  const [newFaq, setNewFaq] = useState<Partial<FAQItem>>({});
  const [newTag, setNewTag] = useState('');
  const [newGalleryUrl, setNewGalleryUrl] = useState('');
  const [bulkGalleryUrls, setBulkGalleryUrls] = useState('');
  
  // Google Drive Sync State
  const [driveFolderUrl, setDriveFolderUrl] = useState('');
  const [isScanning, setIsScanning] = useState(false);

  const handleAddBlog = () => {
    if (!newBlog.title) return;
    const item: BlogPost = {
      id: Date.now().toString(),
      title: newBlog.title || '',
      excerpt: newBlog.excerpt || '',
      content: newBlog.content || '',
      images: newBlog.images && newBlog.images.length > 0 ? newBlog.images : ['https://picsum.photos/800/600'],
      date: new Date().toLocaleDateString(),
      likes: 0,
      tags: newBlog.tags || [],
      isPopular: newBlog.isPopular || false
    };
    const updated = [item, ...blogs];
    setBlogs(updated);
    StorageService.saveBlogs(updated);
    setNewBlog({ tags: [], images: [] });
    onSync();
  };

  const handleAddFaq = () => {
    if (!newFaq.question) return;
    const item: FAQItem = {
      id: Date.now().toString(),
      question: newFaq.question || '',
      answer: newFaq.answer || ''
    };
    const updated = [item, ...faqs];
    setFaqs(updated);
    StorageService.saveFAQs(updated);
    setNewFaq({});
    onSync();
  };

  const handleAddGalleryItem = () => {
    if (!newGalleryUrl) return;
    const item: GalleryImage = {
      id: Date.now().toString(),
      url: newGalleryUrl,
      title: 'New Image'
    };
    const updated = [item, ...gallery];
    setGallery(updated);
    StorageService.saveGallery(updated);
    setNewGalleryUrl('');
    onSync();
  };

  const handleBulkAddGallery = () => {
    const urls = bulkGalleryUrls.split('\n').map(u => u.trim()).filter(u => u.length > 0);
    if (urls.length === 0) return;

    const newItems: GalleryImage[] = urls.map((url, index) => ({
      id: (Date.now() + index).toString(),
      url: url,
      title: 'Bulk Image'
    }));

    const updated = [...newItems, ...gallery];
    setGallery(updated);
    StorageService.saveGallery(updated);
    setBulkGalleryUrls('');
    onSync();
  };

  const handleGoogleDriveSync = async () => {
    if (!googleKey) {
      alert('Please set your Google API Key in Settings first.');
      return;
    }

    // Extract Folder ID from URL if necessary
    let folderId = driveFolderUrl;
    const match = driveFolderUrl.match(/[-\w]{25,}/);
    if (match) folderId = match[0];

    if (!folderId) {
      alert('Invalid Folder ID or Link');
      return;
    }

    setIsScanning(true);
    try {
      const response = await fetch(`https://www.googleapis.com/drive/v3/files?q='${folderId}'+in+parents+and+mimeType+contains+'image/'&key=${googleKey}&fields=files(id,name,mimeType)`);
      const data = await response.json();

      if (data.error) {
        throw new Error(data.error.message);
      }

      if (data.files && data.files.length > 0) {
        const newImages: GalleryImage[] = data.files.map((file: any, index: number) => ({
          id: (Date.now() + index).toString(),
          url: `https://lh3.googleusercontent.com/d/${file.id}`,
          title: file.name
        }));

        const updated = [...newImages, ...gallery];
        setGallery(updated);
        StorageService.saveGallery(updated);
        alert(`Successfully imported ${newImages.length} images from Google Drive!`);
        setDriveFolderUrl('');
      } else {
        alert('No images found in this folder.');
      }
    } catch (error: any) {
      alert(`Error scanning folder: ${error.message}`);
    } finally {
      setIsScanning(false);
      onSync();
    }
  };

  const deleteGalleryItem = (id: string) => {
    const updated = gallery.filter(g => g.id !== id);
    setGallery(updated);
    StorageService.saveGallery(updated);
    onSync();
  };

  const toggleImageInBlog = (url: string) => {
    const currentImages = newBlog.images || [];
    if (currentImages.includes(url)) {
      setNewBlog({ ...newBlog, images: currentImages.filter(i => i !== url) });
    } else {
      setNewBlog({ ...newBlog, images: [...currentImages, url] });
    }
  };

  const handleSaveSettings = () => {
    StorageService.saveGoogleKey(googleKey);
    alert('Settings saved!');
  };

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-8">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Admin Dashboard</h1>
          <p className="text-stone-500">Live Sync with Stealth Database</p>
        </div>
        <div className="flex space-x-4">
          <button onClick={onExport} className="bg-stone-900 text-white px-6 py-2 rounded-xl hover:bg-stone-800 transition-colors uppercase text-xs font-bold tracking-widest">Export Leads</button>
        </div>
      </div>

      <div className="flex flex-wrap gap-4 border-b border-stone-200">
        {['leads', 'blog', 'faq', 'tags', 'gallery', 'newsletter', 'settings'].map((tab) => (
          <button 
            key={tab}
            onClick={() => setActiveTab(tab as any)}
            className={`pb-4 px-2 uppercase text-xs font-bold tracking-widest transition-all ${activeTab === tab ? 'border-b-2 border-stone-900 text-stone-900' : 'text-stone-400'}`}
          >
            {tab}
          </button>
        ))}
      </div>

      {activeTab === 'leads' && (
        <div className="bg-white rounded-2xl shadow-sm border border-stone-200 overflow-hidden">
          <table className="w-full text-left">
            <thead className="bg-stone-50 border-b border-stone-200">
              <tr>
                <th className="px-6 py-4 text-[10px] uppercase text-stone-500 font-bold">Date</th>
                <th className="px-6 py-4 text-[10px] uppercase text-stone-500 font-bold">Client</th>
                <th className="px-6 py-4 text-[10px] uppercase text-stone-500 font-bold">Interest</th>
                <th className="px-6 py-4 text-[10px] uppercase text-stone-500 font-bold">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {leads.map(lead => (
                <tr key={lead.id} className={lead.status === 'new' ? 'bg-emerald-50/30' : ''}>
                  <td className="px-6 py-4 text-sm">{new Date(lead.timestamp).toLocaleDateString()}</td>
                  <td className="px-6 py-4">
                    <div className="font-bold">{lead.name}</div>
                    <div className="text-xs text-stone-400">{lead.email}</div>
                  </td>
                  <td className="px-6 py-4 text-xs font-bold uppercase">{lead.interest}</td>
                  <td className="px-6 py-4">
                    <button onClick={() => onUpdateStatus(lead.id, 'read')} className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase ${lead.status === 'new' ? 'bg-emerald-900 text-white' : 'bg-stone-100 text-stone-400'}`}>
                      {lead.status}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'gallery' && (
        <div className="space-y-8">
          <div className="grid lg:grid-cols-2 gap-8">
             {/* Google Drive Scanner Card */}
             <div className="bg-emerald-50/30 p-8 rounded-2xl border border-emerald-100 space-y-4">
                <div className="flex items-center space-x-3 mb-2">
                  <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center text-white">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/></svg>
                  </div>
                  <h3 className="font-bold uppercase tracking-widest text-xs text-emerald-900">Google Drive Scanner</h3>
                </div>
                <p className="text-[10px] text-emerald-700 uppercase font-bold">Auto-import all photos from a public folder</p>
                <div className="flex space-x-4">
                  <input 
                    placeholder="Paste Folder Link or ID..." 
                    value={driveFolderUrl} 
                    onChange={e => setDriveFolderUrl(e.target.value)} 
                    className="flex-grow p-3 bg-white/50 rounded-xl border border-emerald-200 text-sm outline-none focus:ring-2 focus:ring-emerald-500/20" 
                  />
                  <button 
                    onClick={handleGoogleDriveSync} 
                    disabled={isScanning}
                    className="bg-emerald-600 text-white px-8 rounded-xl font-bold uppercase text-xs hover:bg-emerald-700 transition-colors disabled:opacity-50"
                  >
                    {isScanning ? 'Scanning...' : 'Scan Folder'}
                  </button>
                </div>
             </div>

             <div className="bg-white p-8 rounded-2xl border border-stone-200 space-y-4">
              <h3 className="font-bold uppercase tracking-widest text-xs text-stone-400">Bulk Add (Manual URLs)</h3>
              <div className="space-y-4">
                <textarea 
                  placeholder="Paste direct links (one per line)..." 
                  value={bulkGalleryUrls} 
                  onChange={e => setBulkGalleryUrls(e.target.value)} 
                  className="w-full p-3 bg-stone-50 rounded-xl border-none text-sm h-24"
                />
                <button onClick={handleBulkAddGallery} className="w-full bg-stone-900 text-white p-3 rounded-xl font-bold uppercase text-xs">Add All Links</button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {gallery.map(img => (
              <div key={img.id} className="group relative aspect-square bg-stone-100 rounded-2xl overflow-hidden border border-stone-200">
                <img src={img.url} className="w-full h-full object-cover" />
                <button 
                  onClick={() => deleteGalleryItem(img.id)}
                  className="absolute top-2 right-2 bg-red-500 text-white p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'settings' && (
        <div className="bg-white p-10 rounded-2xl border border-stone-200 space-y-8 max-w-2xl">
          <div className="space-y-2">
            <h3 className="text-xl font-bold uppercase tracking-tight">API Configuration</h3>
            <p className="text-stone-500 text-sm">Configure external services for automation.</p>
          </div>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-[10px] font-bold uppercase text-stone-400 tracking-widest">Google Cloud API Key</label>
              <input 
                type="password" 
                placeholder="Enter your API Key..." 
                value={googleKey} 
                onChange={e => setGoogleKey(e.target.value)} 
                className="w-full p-4 bg-stone-50 rounded-xl border-none text-sm font-mono" 
              />
              <div className="p-4 bg-stone-50 rounded-xl text-[10px] text-stone-500 leading-relaxed">
                <strong>How to get this key:</strong>
                <ol className="list-decimal ml-4 mt-2 space-y-1">
                  <li>Go to <a href="https://console.cloud.google.com/" target="_blank" className="underline">Google Cloud Console</a>.</li>
                  <li>Create a new project.</li>
                  <li>Go to "APIs & Services" &gt; "Library" and enable "Google Drive API".</li>
                  <li>Go to "Credentials" &gt; "Create Credentials" &gt; "API Key".</li>
                  <li>Copy the key and paste it here.</li>
                </ol>
                <p className="mt-2 text-emerald-600">Note: The folder in Google Drive must be set to "Anyone with the link can view".</p>
              </div>
            </div>
            
            <button onClick={handleSaveSettings} className="bg-stone-900 text-white px-10 py-4 rounded-xl font-bold uppercase text-xs tracking-widest hover:bg-stone-800 transition-all">Save Settings</button>
          </div>
        </div>
      )}

      {/* Other tabs follow same pattern as before... */}
      {activeTab === 'blog' && (
        <div className="grid lg:grid-cols-2 gap-8">
          <div className="bg-white p-8 rounded-2xl border border-stone-200 space-y-6">
            <h3 className="font-bold uppercase tracking-widest text-sm">Create New Article</h3>
            <input placeholder="Title" value={newBlog.title || ''} onChange={e => setNewBlog({...newBlog, title: e.target.value})} className="w-full p-3 bg-stone-50 rounded-xl border-none text-sm" />
            <textarea placeholder="Excerpt" value={newBlog.excerpt || ''} onChange={e => setNewBlog({...newBlog, excerpt: e.target.value})} className="w-full p-3 bg-stone-50 rounded-xl border-none text-sm h-24" />
            <textarea placeholder="Full Content" value={newBlog.content || ''} onChange={e => setNewBlog({...newBlog, content: e.target.value})} className="w-full p-3 bg-stone-50 rounded-xl border-none text-sm h-48" />
            
            <div className="space-y-2">
              <label className="text-[10px] font-bold uppercase text-stone-400">Select Images from Gallery</label>
              <div className="grid grid-cols-4 gap-2 max-h-40 overflow-y-auto p-2 bg-stone-50 rounded-xl">
                {gallery.map(img => (
                  <div 
                    key={img.id} 
                    onClick={() => toggleImageInBlog(img.url)}
                    className={`relative aspect-square cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${newBlog.images?.includes(img.url) ? 'border-emerald-500 scale-95' : 'border-transparent'}`}
                  >
                    <img src={img.url} className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            </div>

            <button onClick={handleAddBlog} className="w-full bg-stone-900 text-white p-4 rounded-xl font-bold uppercase text-xs tracking-widest">Publish Article</button>
          </div>
          <div className="space-y-4">
            <h3 className="font-bold uppercase tracking-widest text-sm">Existing Articles</h3>
            {blogs.map(blog => (
              <div key={blog.id} className="bg-white p-4 rounded-xl border border-stone-200 flex justify-between items-center">
                <div className="font-bold text-sm">{blog.title}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {activeTab === 'newsletter' && (
        <div className="bg-white rounded-2xl shadow-sm border border-stone-200 overflow-hidden">
          <table className="w-full text-left">
            <thead className="bg-stone-50 border-b border-stone-200">
              <tr>
                <th className="px-6 py-4 text-[10px] uppercase text-stone-500 font-bold">Email Address</th>
                <th className="px-6 py-4 text-[10px] uppercase text-stone-500 font-bold">Joined Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {subscribers.map(sub => (
                <tr key={sub.id}>
                  <td className="px-6 py-4 font-bold">{sub.email}</td>
                  <td className="px-6 py-4 text-sm text-stone-400">{new Date(sub.timestamp).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
